import type { Command } from "@/services/runcli";

export const exampleCommands: Command[] = [
  // Boards
  {
    category: "Board Examples",
    id: "listBoards",
    shortDescription: "Get a list of boards",
    longDescription: "Use this command to see a list of boards in the current workspace.",
    text: "--action getBoardList --workspace {{workspaceId}} --bestView",
    note: "",
  },
  {
    category: "Board Examples",
    id: "createBoard",
    shortDescription: "Create a board",
    longDescription: "Use this command to create a board in the current workspace.",
    text: '--action addBoard --workspace {{workspaceId}} --type public --board "New Board"',
    note: "",
  },
  {
    category: "Board Examples",
    id: "copyBoard",
    shortDescription: "Copy a board",
    longDescription: "Use this command to make a copy of a board, its structure, and all items.",
    text: '--action copyBoard --workspace {{workspaceId}} --board {{boardId}} --name "Copied Board" --copyItems',
    note: "",
  },
  // Items
  {
    category: "Item Examples",
    id: "listItems",
    shortDescription: "Get a list of items on a board",
    longDescription: "Use this command to get a list of items on the current board.",
    text: "--action getItemList --workspace {{workspaceId}} --board {{boardId}} --bestView",
    note: 'Notice the `--bestView` parameter used in this example command. This parameter can be used with "list" actions. It creates compact, easy-to-read content in the output by limiting the columns shown to the most likely needed.',
  },
  {
    category: "Item Examples",
    id: "addItem",
    shortDescription: "Add an item",
    longDescription: "Use this command to add a new item to the current board.",
    text: '--action addItem --workspace {{workspaceId}} --board {{boardId}} --item "New Item"',
    note: "",
  },
  {
    category: "Item Examples",
    id: "updateItem",
    shortDescription: "Update item status",
    longDescription: 'Use this command to update the status of an item (see "get a list" example above for item id).',
    text: '--action updateItem --board {{boardId}} --item "[enter item name or id here]" --field status=Done',
    note: "",
  },
  // Groups
  {
    category: "Group Examples",
    id: "listGroups",
    shortDescription: "Get a list of groups on a board",
    longDescription: "Use this command to see a list of groups from the current board.",
    text: "--action getGroupList --workspace {{workspaceId}} --board {{boardId}}",
    note: "",
  },
  {
    category: "Group Examples",
    id: "addGroup",
    shortDescription: "Add a group",
    longDescription: "Use this command to add a new group to the bottom of the current board.",
    text: '--action addGroup --workspace {{workspaceId}} --board {{boardId}} --group "New Group"',
    note: "",
  },
  {
    category: "Group Examples",
    id: "listGroupItems",
    shortDescription: "Get a list of items in a group",
    longDescription: "Use this command to retrieve a list of items in a specific group from the current board.",
    text: '--action getItemList --workspace {{workspaceId}} --board {{boardId}} --bestView --select "Group name:Group B"',
    note: 'This example uses the `--select` parameter which allows the list of items returned to be filtered by the field specified in the parameter. In this example only items that are in "Group B" will be returned.',
  },
  {
    category: "User Examples",
    id: "getUser",
    shortDescription: "Get user information",
    longDescription: "Use this command to get information about a user.",
    text: "--action getUser --user @self",
    note: "",
  },
  {
    category: "User Examples",
    id: "listUsers",
    shortDescription: "Get a list of users",
    longDescription: "Use this command to get a list of users.",
    text: "--action getUserList --limit 50 --bestView",
    note: "This example uses the `--limit` parameter. This controls the number of items returned by a list. By default most lists are limited to 100 items. However, this limit can be raised using the limit parameter.",
  },
  {
    category: "User Examples",
    id: "getUsersInfo",
    shortDescription: "Get information from a list of users",
    longDescription:
      "Use this command to iterate over a filtered list of users and get additional information about each user.",
    text: '--action runFromUserList --regex "Bob.*" --input "--action getUser --user @user@"',
    note: "Notice the use of the nested action provided by the `--input` parameter, which will be run once for every user that matches the regex. The `@user@` parameter is replaced with the user name.",
  },
  {
    category: "User Examples",
    id: "addUser",
    shortDescription: "Add a user to a workspace",
    longDescription: "This command will add a user to a workspace.",
    text: "--action addUserToWorkspace --user @self --workspace @default",
    note: "",
    // Teams
  },
  {
    category: "Team Examples",
    id: "listTeams",
    shortDescription: "Get a list of teams",
    longDescription: "Use this command to get a list of teams.",
    text: "--action getTeamList --bestView",
    note: "",
  },
  {
    category: "Team Examples",
    id: "addTeam",
    shortDescription: "Add a team to a workspace",
    longDescription: "Use this command to add a team to a workspace.",
    text: '--action addTeamToWorkspace --team "ACLI fan club" --workspace {{workspaceId}}',
    note: "",
  },
  {
    category: "Team Examples",
    id: "removeTeam",
    shortDescription: "Remove a team from a workspace",
    longDescription: "Use this command to remove a team from a workspace.",
    text: '--action removeTeamFromWorkspace --team "ACLI fan club" --workspace {{workspaceId}}',
    note: "",
  },
  // Workspace
  {
    category: "Workspace Examples",
    id: "listWorkspaces",
    shortDescription: "Get a list of workspaces",
    longDescription: "Use this command to retrieve a list of workspaces.",
    text: "--action getWorkspaceList --limit 100 --bestView",
    note: "",
  },
  {
    category: "Workspace Examples",
    id: "addWorkspace",
    shortDescription: "Add a new workspace",
    longDescription: "Use this command to create a new workspace.",
    text: '--action addWorkspace --type open --workspace "ACLI Created Workspace"',
    note: "",
  },
  {
    category: "Workspace Examples",
    id: "getWorkspace",
    shortDescription: "Get workspace information",
    longDescription: "Use this command to get information about a workspace.",
    text: "--action getWorkspace --workspace @default",
    note: "",
  },
  {
    category: "Workspace Examples",
    id: "filterWorkspaces",
    shortDescription: "Get a filtered list of workspaces",
    longDescription: "Use this command to retrieve a filtered list of workspaces.",
    text: "--action getWorkspaceList --regex .*workspace --outputType text --columns workspace,name,description",
    note: "",
  },
];
